import React from 'react';
import { Poppins } from 'src/common';
import { VariableCapsule } from './comps/VariableCapsule';
import { Variable } from 'src/api/types';
import { formatCurrency } from 'src/utils/misc';
import { isWorkspaceOrGlobalVariable } from 'src/components/FormulaInput/shared/formula';

interface ParseOptions {
  showValueOnHover?: boolean;
  tooltipId?: string;
  /** When set, each resolved variable pill becomes clickable and reports its id. */
  onVariableClick?: (variableId: number) => void;
}

/**
 * True when the formula is a single plain number (no variables or operators).
 * Mirrors the `parseToJsx` short-circuit below: such formulas render as a bare
 * formatted number, so callers can choose to hide a dedicated "formula" section
 * and only show it for an actual expression or variable.
 */
export const isPlainNumberFormula = (formula?: string | number | null): boolean =>
  formula != null && formula !== '' && !isNaN(Number(formula));

export const parseToJsx = (formula: string | number, variables: Variable[], opts?: ParseOptions) => {
  if (typeof formula === 'number' || !isNaN(Number(formula))) {
    return formatCurrency(Number(formula));
  }

  // Workspace/global names → lets getVariableIcon flag assessment overrides.
  const workspaceVariableNames = new Set(variables.filter(isWorkspaceOrGlobalVariable).map((v) => v.name));

  const parts: React.ReactNode[] = [];
  let currentPart = '';

  const flush = () => {
    if (currentPart) {
      parts.push(
        <Poppins px={14} weight={500} key={parts.length}>
          {currentPart}
        </Poppins>,
      );
      currentPart = '';
    }
  };

  for (let i = 0; i < formula.length; i++) {
    const char = formula[i];

    if (char === '#' && i < formula.length - 1) {
      flush();
      let id = '';
      i++;
      while (i < formula.length && /[A-Za-z0-9_]/.test(formula[i])) {
        id += formula[i];
        i++;
      }
      i--;

      const idx = parts.length;
      parts.push(
        <VariableCapsule
          key={idx}
          id={id}
          variables={variables}
          workspaceVariableNames={workspaceVariableNames}
          showValueOnHover={opts?.showValueOnHover}
          tooltipId={opts?.tooltipId}
          onVariableClick={opts?.onVariableClick}
        />,
      );
    } else if (/[\+\-\*\/\(\)]/.test(char)) {
      flush();
      parts.push(
        <Poppins px={14} weight={500} key={parts.length}>
          {char}
        </Poppins>,
      );
    } else {
      currentPart += char;
    }
  }

  flush();

  return parts;
};
